import { useEffect, useRef, useMemo, PropsWithChildren, memo, forwardRef } from 'react'
import { FeatureHighlightProps, useFeatureHighlights, useOpenModals } from '../../store'
import { Popover } from '@highlight/ui'
import { twMerge } from 'tailwind-merge'
import { Button } from '@highlight/ui'

export const FeatureHighlight = memo(
  forwardRef<HTMLDivElement, PropsWithChildren<FeatureHighlightProps>>(
    (
      {
        children,
        description,
        id,
        nextId,
        previousId,
        hide,
        offset = 0,
        position,
        alignment,
        title,
        primaryAction,
        secondaryAction,
        style,
        backgroundOverlay,
        dismissible,
        delay,
        wrapperStyle,
        className,
        activeWrapperClassName,
        wrapperClassName,
        videoUrl,
        hint,
        disabled,
        disabledWithModals,
      },
      ref,
    ) => {
      const modals = useOpenModals()
      const { activeFeatureHighlightId, queueFeatureHighlight, dismissFeatureHighlight } = useFeatureHighlights()
      const initialHideRef = useRef(hide)

      const isActive = useMemo(() => {
        if (hide || disabled) {
          return false
        }
        return activeFeatureHighlightId === id
      }, [activeFeatureHighlightId, id, hide, disabled])

      useEffect(() => {
        const queue = () => {
          queueFeatureHighlight(id, {
            previousId,
            nextId,
          })
        }

        let timeout: ReturnType<typeof setTimeout> | null = null
        if (delay) {
          timeout = setTimeout(() => {
            queue()
            timeout = null
          }, delay)
        } else {
          queue()
        }

        return () => {
          if (timeout) {
            clearTimeout(timeout)
          }
        }
      }, [id, previousId, nextId, delay, queueFeatureHighlight])

      useEffect(() => {
        if (hide && initialHideRef.current !== hide && dismissible) {
          initialHideRef.current = hide
          dismissFeatureHighlight(id, { nextId })
        }
      }, [hide])

      if (disabled || (modals.length > 0 && disabledWithModals)) {
        return typeof children === 'function'
          ? // @ts-ignore
            children({ isActive })
          : children
      }

      return (
        <>
          {isActive && backgroundOverlay !== false && (
            <div
              className={
                'animate-fadeIn fixed left-0 top-0 z-[1] h-screen w-screen rounded-3xl bg-black/70 backdrop-blur transition-opacity'
              }
            />
          )}
          <Popover open={isActive} onOpenChange={(_open: boolean) => {}}>
            <Popover.Trigger
              render={<div />}
              nativeButton={false}
              // @ts-ignore
              ref={ref}
              className={twMerge(
                'bg-transparent p-0 outline-none',
                wrapperClassName,
                isActive ? activeWrapperClassName : '',
              )}
              style={wrapperStyle}
            >
              {typeof children === 'function'
                ? // @ts-ignore
                  children({ isActive })
                : children}
            </Popover.Trigger>
            <Popover.Content
              side={position}
              sideOffset={offset}
              align={alignment}
              className={twMerge('flex flex-col gap-3 p-4 shadow-xl', className)}
              style={style}
            >
              {videoUrl && <video src={videoUrl} className={'w-full'} autoPlay={true} muted={true} loop={true} />}
              <div className={'relative flex w-full flex-col gap-2'}>
                <Popover.Title>{title}</Popover.Title>
                <Popover.Description>{description}</Popover.Description>
                {hint && <span className={'absolute right-0 top-0 text-xs text-white/30'}>{hint}</span>}
              </div>
              {(primaryAction || secondaryAction) && (
                <div className={'flex w-full items-center gap-3'}>
                  {secondaryAction && (
                    <Button
                      variant="outline"
                      onClick={(e) => {
                        secondaryAction.onClick?.(e)
                        if (secondaryAction.dismiss !== false) {
                          dismissFeatureHighlight(id, { nextId })
                        }
                      }}
                      className={'flex-1'}
                    >
                      {secondaryAction.label}
                    </Button>
                  )}
                  {primaryAction && (
                    <Button
                      variant="primary"
                      onClick={(e) => {
                        primaryAction.onClick?.(e)
                        if (primaryAction.dismiss !== false) {
                          dismissFeatureHighlight(id, { nextId })
                        }
                      }}
                      className={'flex-1'}
                    >
                      {primaryAction.label}
                    </Button>
                  )}
                </div>
              )}
            </Popover.Content>
          </Popover>
        </>
      )
    },
  ),
)
FeatureHighlight.displayName = 'FeatureHighlight'
